import React from "react";
import Layout from "layout";
import Loader from "shared/Loader";
import { useGetSuppliersPerformanceQuery } from "api/productApi";
import {
  PageRoot,
  PageHeader,
  PageTitle,
  PageSub,
  SupplierGrid,
  SupplierCard,
  CardTop,
  SupplierMain,
  SupplierAvatar,
  SupplierName,
  SupplierMeta,
  VerifiedBadge,
  MetricsGrid,
  MetricCard,
  MetricValue,
  MetricLabel,
  EmptyState,
} from "./styles";

const toneColor = (tone) => {
  if (tone === "good") return "#16a34a";
  if (tone === "warn") return "#E8521A";
  return "#9ca3af";
};

const METRICS = [
  { key: "onTimeShip", label: "On-Time Shipping" },
  { key: "trackingAccuracy", label: "Tracking Accuracy" },
  { key: "cancelRate", label: "Cancel Rate" },
  { key: "processing", label: "Processing Load" },
];

const SupplierDashboard = () => {
  const { data, isLoading, isFetching, error } = useGetSuppliersPerformanceQuery();
  const suppliers = data?.suppliers || [];

  return (
    <PageRoot>
      <PageHeader>
        <PageTitle>Supplier Dashboard</PageTitle>
        <PageSub>
          Latest performance across all platform suppliers — on-time shipping, tracking
          accuracy, and fulfillment health updated from real order data.
        </PageSub>
      </PageHeader>

      {isLoading || isFetching ? (
        <Loader isLoading />
      ) : error ? (
        <EmptyState>Could not load supplier performance. Please try again later.</EmptyState>
      ) : suppliers.length === 0 ? (
        <EmptyState>No suppliers with listed products yet.</EmptyState>
      ) : (
        <SupplierGrid>
          {suppliers.map((supplier) => (
            <SupplierCard key={supplier.id}>
              <CardTop>
                <SupplierMain>
                  <SupplierAvatar
                    style={
                      supplier.profileUrl
                        ? {
                            backgroundImage: `url(${supplier.profileUrl})`,
                            backgroundColor: "transparent",
                          }
                        : undefined
                    }
                  >
                    {!supplier.profileUrl ? supplier.initials : ""}
                  </SupplierAvatar>
                  <div>
                    <SupplierName>{supplier.displayName}</SupplierName>
                    <SupplierMeta>
                      {supplier.productCount} product
                      {supplier.productCount === 1 ? "" : "s"} listed
                    </SupplierMeta>
                  </div>
                </SupplierMain>
                <VerifiedBadge $muted={!supplier.verified}>
                  {supplier.verified ? "✓ Platform Verified" : "Not verified"}
                </VerifiedBadge>
              </CardTop>

              <MetricsGrid>
                {METRICS.map(({ key, label }) => {
                  const metric = supplier.stats?.[key];
                  return (
                    <MetricCard key={key}>
                      <MetricValue $tone={toneColor(metric?.tone)}>
                        {metric?.display ?? "—"}
                      </MetricValue>
                      <MetricLabel>{label}</MetricLabel>
                    </MetricCard>
                  );
                })}
              </MetricsGrid>
            </SupplierCard>
          ))}
        </SupplierGrid>
      )}
    </PageRoot>
  );
};

const SupplierDashboardPage = () => (
  <Layout>
    <SupplierDashboard />
  </Layout>
);

export default SupplierDashboardPage;
